import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, CalendarIcon, MapPin, Type, FileText, Sparkles, X, Check, Clock, RefreshCw, AlertCircle } from "lucide-react";
import { toast, Toaster } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { API, getAuthHeaders, hasToken } from "@/lib/api";



const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

const THEME = {
  bg: "#121212",
  bgSubtle: "var(--brand-text)",
  accent: "var(--brand-accent)",
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

const EVENT_TYPES = [
  { value: "wedding", label: "Hochzeit", emoji: "💒", image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&q=80" },
  { value: "birthday", label: "Geburtstag", emoji: "🎂", image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80" },
  { value: "party", label: "Party", emoji: "🎉", image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80" },
  { value: "graduation", label: "Abschluss", emoji: "🎓", image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&q=80" },
  { value: "anniversary", label: "Jubiläum", emoji: "💝", image: "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=400&q=80" },
  { value: "baby_shower", label: "Babyparty", emoji: "👶", image: "https://images.unsplash.com/photo-1519611019068-bfe002796a7c?w=400&q=80" },
  { value: "corporate", label: "Firmenevent", emoji: "🏢", image: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&q=80" },
  { value: "christmas", label: "Weihnachten", emoji: "🎄", image: "https://images.unsplash.com/photo-1543589077-47d81606c1bf?w=400&q=80" },
  { value: "new_year", label: "Silvester", emoji: "🎆", image: "https://images.unsplash.com/photo-1467810563316-b5476525c0f9?w=400&q=80" },
  { value: "easter", label: "Ostern", emoji: "🐰", image: "https://images.unsplash.com/photo-1522336572018-088860bc4631?w=400&q=80" },
  { value: "valentine", label: "Valentinstag", emoji: "💝", image: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=400&q=80" },
  { value: "other", label: "Sonstiges", emoji: "🎈", image: "https://images.unsplash.com/photo-1472653431118-c97962b4adad?w=400&q=80" },
];

const STEPS = [
  { id: 'category', title: 'Kategorie', subtitle: 'Was möchtest du planen?' },
  { id: 'details', title: 'Details', subtitle: 'Wie heißt dein Event?' },
  { id: 'date', title: 'Zeitraum', subtitle: 'Wann findet es statt?' },
  { id: 'guests', title: 'Gäste', subtitle: 'Wen möchtest du einladen?' },
  { id: 'vision', title: 'Vision', subtitle: 'Beschreibe dein Event' }
];


export default function CreateEvent({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0); // -1 for back, 1 for next
  // locationInputRef no longer needed here as it's internal to the new component

  const [formData, setFormData] = useState({
    title: "",
    event_type: "",
    date: null,
    location: "",
    description: "",
    guests: [] // Array of {name, email}
  });

  const [guestNameInput, setGuestNameInput] = useState("");
  const [guestEmailInput, setGuestEmailInput] = useState("");


  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token && !user) {
      console.warn("⚠️ No authentication found, redirecting to login");
      toast.error("Bitte melde dich an, um ein Event zu erstellen");
      navigate("/login");
    }
  }, [user, navigate]);

  // Check event limit on mount
  useEffect(() => {
    const checkEventLimit = async () => {
      try {
        const response = await axios.get(`${API}/user/can-create-event`, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`
          }
        });

        if (!response.data.can_create) {
          setLimitInfo(response.data);
          setShowLimitModal(true);
        }
      } catch (error) {
        console.error("Error checking event limit:", error);
        // If check fails, allow creation (fail-open)
      }
    };

    if (user || localStorage.getItem("auth_token")) {
      checkEventLimit();
    }
  }, [user, navigate]);



  // Old useEffect for maps is removed/replaced by key-based step rendering
  // ...

  const handleAddGuest = () => {
    if (!guestNameInput.trim()) {
      toast.error("Bitte gib mindestens einen Namen ein");
      return;
    }

    // Simple email validation if provided
    if (guestEmailInput.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestEmailInput.trim())) {
        toast.error("Ungültige E-Mail-Adresse");
        return;
      }
    }

    const newGuest = {
      name: guestNameInput.trim(),
      email: guestEmailInput.trim() || null
    };

    if (guestEmailInput.trim() && formData.guests.some(g => g.email === guestEmailInput.trim())) {
      toast.error("Diese E-Mail wurde bereits hinzugefügt");
      return;
    }

    setFormData(prev => ({
      ...prev,
      guests: [...prev.guests, newGuest]
    }));
    setGuestNameInput("");
    setGuestEmailInput("");
  };

  const handleRemoveGuest = (index) => {
    setFormData(prev => ({
      ...prev,
      guests: prev.guests.filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    // Validation is now purely state-based since LocationAutocomplete updates state on blur/enter
    if (validateStep(step)) {
      setDirection(1);
      setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    } else {
      toast.error("Bitte fülle alle Felder aus", { position: "top-center" });
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const validateStep = (currentStep, data = formData) => {
    switch (currentStep) {
      case 0: return !!data.event_type;
      case 1: return !!data.title && !!data.location;
      case 2: return !!data.date;
      default: return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🔍 Event Creation Debug:");
    console.log("Form Data:", formData);
    console.log("Auth Token:", localStorage.getItem("auth_token") ? "Present" : "Missing");
    console.log("User:", user);

    if (!formData.title || !formData.event_type || !formData.date || !formData.location) {
      console.error("❌ Validation failed:", {
        title: !!formData.title,
        event_type: !!formData.event_type,
        date: !!formData.date,
        location: !!formData.location
      });
      toast.error("Bitte überprüfe deine Eingaben");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: formData.date.toISOString(),
      };

      console.log("📤 Sending payload:", payload);
      console.log("📍 API URL:", `${API}/events`);
      console.log("🔐 Auth Config:", authConfig());

      const response = await axios.post(
        `${API}/events`,
        payload,
        authConfig()
      );

      console.log("✅ Success response:", response.data);
      toast.success("Event erfolgreich erstellt!");
      navigate(`/event/${response.data.id}`);
    } catch (error) {
      console.error("❌ Event creation failed:");
      console.error("Error object:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      console.error("Response headers:", error.response?.headers);

      // Handle 403 specifically
      if (error.response?.status === 403) {
        const errorData = error.response?.data;

        // Check if it's a limit error with detailed message
        if (errorData?.detail?.error === "event_limit_reached") {
          const { message, current, max, package: packageName } = errorData.detail;
          toast.error(
            <div className="space-y-2">
              <p className="font-bold">Event-Limit erreicht!</p>
              <p>{message}</p>
              <p className="text-sm">Aktuell: {current}/{max} Events ({packageName})</p>
            </div>,
            { duration: 6000 }
          );
          // Optionally redirect to upgrade page
          setTimeout(() => navigate("/packages"), 3000);
        } else if (errorData?.detail?.message) {
          // Generic detailed error message
          toast.error(errorData.detail.message, { duration: 5000 });
        } else {
          // Fallback for session expired
          toast.error("Sitzung abgelaufen. Bitte melde dich erneut an.");
          localStorage.removeItem("auth_token");
          navigate("/login");
        }
      } else {
        // Generic error
        const errorMessage = error.response?.data?.detail?.message ||
          error.response?.data?.detail ||
          "Event konnte nicht erstellt werden";
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const GlobalBackground = () => (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[100px] -mr-32 -mt-32" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-[100px] -ml-32 -mb-32" />
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
    </div>
  );

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((s, idx) => (
        <div key={s.id} className={`h-1.5 rounded-full transition-all duration-500 ${idx === step ? "w-8 bg-brand-accent" : idx < step ? "w-2 bg-brand-accent/50" : "w-2 bg-slate-200"}`} />
      ))}
    </div>
  );

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95
    })
  };

  return (
    <div className="min-h-screen text-slate-900 font-['Plus_Jakarta_Sans'] relative overflow-x-hidden antialiased bg-slate-50 flex flex-col">
      <GlobalBackground />
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="relative z-50 px-6 py-6 max-w-xl mx-auto w-full flex items-center justify-between">
        <button
          onClick={() => step > 0 ? handleBack() : navigate(-1)}
          className="w-10 h-10 bg-white/80 backdrop-blur-xl rounded-full border border-slate-200 flex items-center justify-center text-slate-900 hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all shadow-sm active:scale-95"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-center">
          <h1 className="text-lg font-black tracking-tight" style={{ fontFamily: THEME.fontSans }}>
            {STEPS[step].title}
          </h1>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
            Schritt {step + 1} von {STEPS.length}
          </p>
        </div>
        <div className="w-10" /> {/* Spacer */}
      </header>

      {/* Main Content Area */}
      <main className="relative z-10 flex-1 flex flex-col max-w-xl mx-auto w-full px-6 pb-24">
        <StepIndicator />

        <div className="flex-1 relative">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="w-full"
            >
              {/* Step 0: Category */}
              {step === 0 && (
                <div className="space-y-6 text-center">
                  <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: THEME.fontSerif }}>
                    {STEPS[0].subtitle}
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {EVENT_TYPES.map((type) => {
                      const isSelected = formData.event_type === type.value;
                      return (
                        <button
                          key={type.value}
                          onClick={() => {
                            setFormData({ ...formData, event_type: type.value });
                            // Auto advance on selection for smoother feel
                            setTimeout(() => {
                              setDirection(1);
                              setStep(1);
                            }, 150);
                          }}
                          className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-3 transition-all duration-300 relative
                                      ${isSelected
                              ? "bg-brand-accent text-white shadow-xl scale-[1.02]"
                              : "bg-white border border-slate-100 hover:border-brand-accent/30 hover:bg-slate-50"
                            }`}
                        >
                          <span className="text-3xl">{type.emoji}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isSelected ? "text-white" : "text-slate-500"}`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 1: Details */}
              {step === 1 && (
                <div className="space-y-8">
                  <h2 className="text-2xl font-bold text-center text-slate-800" style={{ fontFamily: THEME.fontSerif }}>
                    {STEPS[1].subtitle}
                  </h2>
                  <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] p-6 border border-slate-200/60 shadow-lg space-y-6">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider font-bold text-slate-400">Event Name</Label>
                      <Input
                        autoFocus
                        placeholder="z.B. Sommerfest 2024"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="bg-transparent border-none border-b-2 border-slate-200 rounded-none px-0 h-12 text-2xl font-bold text-slate-900 focus-visible:ring-0 focus-visible:border-brand-accent transition-all placeholder:text-slate-300"
                      />
                    </div>
                    <div className="space-y-2 relative">
                      <Label className="text-xs uppercase tracking-wider font-bold text-slate-400">Ort</Label>
                      <div className="flex items-center gap-2 border-b-2 border-slate-200 focus-within:border-brand-accent transition-all">
                        <MapPin className="w-5 h-5 text-slate-400" />
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="Wo wird gefeiert?"
                          onKeyDown={(e) => e.key === "Enter" && handleNext()}
                          className="bg-transparent border-none rounded-none px-0 h-12 text-lg font-medium text-slate-900 focus-visible:ring-0 placeholder:text-slate-300"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleNext}
                      className="w-full bg-slate-900 text-white rounded-xl py-6 hover:bg-slate-800 transition-colors mt-4"
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Date */}
              {step === 2 && (
                <div className="space-y-8 flex flex-col items-center">
                  <h2 className="text-2xl font-bold text-center text-slate-800" style={{ fontFamily: THEME.fontSerif }}>
                    {STEPS[2].subtitle}
                  </h2>
                  <div className="bg-white rounded-[2.5rem] p-6 shadow-xl border border-slate-100">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData({ ...formData, date })}
                      initialFocus
                      locale={de}
                      className="p-0"
                    />
                  </div>
                  {formData.date && (
                    <div className="bg-brand-accent/10 px-6 py-3 rounded-full text-brand-accent font-bold">
                      {format(formData.date, "dd. MMMM yyyy", { locale: de })}
                    </div>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={!formData.date}
                    className="w-full bg-slate-900 text-white rounded-xl py-6 hover:bg-slate-800 transition-colors mt-4"
                  >
                    Weiter
                  </Button>
                </div>
              )}

              {/* Step 3: Guests (NEW) */}
              {step === 3 && (
                <div className="space-y-8">
                  <h2 className="text-2xl font-bold text-center text-slate-800" style={{ fontFamily: THEME.fontSerif }}>
                    {STEPS[3].subtitle}
                  </h2>
                  <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] p-6 border border-slate-200/60 shadow-lg space-y-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider font-bold text-slate-400">Gäste hinzufügen</Label>
                        <div className="flex flex-col gap-2">
                          <Input
                            placeholder="Name (z.B. Max Mustermann)"
                            value={guestNameInput}
                            onChange={(e) => setGuestNameInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddGuest()}
                            className="bg-gray-50 border-gray-200 text-lg h-12 rounded-xl focus-visible:ring-brand-accent focus-visible:border-brand-accent"
                          />
                          <div className="flex gap-2">
                            <Input
                              placeholder="E-Mail (optional)"
                              value={guestEmailInput}
                              onChange={(e) => setGuestEmailInput(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleAddGuest()}
                              className="bg-gray-50 border-gray-200 text-lg h-12 rounded-xl flex-1 focus-visible:ring-brand-accent focus-visible:border-brand-accent"
                            />
                            <Button onClick={handleAddGuest} className="bg-brand-accent text-white h-12 w-12 rounded-xl shrink-0 p-0 flex items-center justify-center">
                              <Check className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 pl-1">Name ist Pflicht, E-Mail optional</p>
                      </div>
                    </div>

                    {/* Guest List */}
                    {formData.guests.length > 0 ? (
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {formData.guests.map((guest, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white border border-gray-100 p-3 rounded-xl shadow-sm">
                            <div className="flex flex-col min-w-0">
                              <span className="font-bold text-gray-900 truncate text-sm">{guest.name}</span>
                              {guest.email && <span className="text-xs text-gray-500 truncate">{guest.email}</span>}
                            </div>
                            <button onClick={() => handleRemoveGuest(idx)} className="text-red-400 hover:text-red-600 bg-red-50 hover:bg-red-100 p-1.5 rounded-lg transition-colors ml-2">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                        Noch keine Gäste hinzugefügt
                      </div>
                    )}

                    <Button
                      onClick={handleNext}
                      className="w-full bg-slate-900 text-white rounded-xl py-6 hover:bg-slate-800 transition-colors mt-4"
                    >
                      {formData.guests.length > 0 ? `Weiter (${formData.guests.length} Gäste)` : "Überspringen"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Vision / Finish */}
              {step === 4 && (
                <div className="space-y-8">
                  <h2 className="text-2xl font-bold text-center text-slate-800" style={{ fontFamily: THEME.fontSerif }}>
                    {STEPS[4].subtitle}
                  </h2>
                  <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] p-6 border border-slate-200/60 shadow-lg space-y-4">
                    <Textarea
                      placeholder="Erzähl uns etwas über deine Vision..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-transparent border-none text-lg text-slate-700 placeholder:text-slate-300 resize-none h-40 focus-visible:ring-0"
                    />
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full h-14 rounded-2xl bg-brand-accent text-white font-bold text-lg uppercase tracking-widest hover:bg-brand-accent/90 transition-all shadow-xl shadow-brand-accent/20 mt-4"
                    >
                      {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : "Event Starten"}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Event Limit Modal */}
      <AnimatePresence>
        {showLimitModal && limitInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-white text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white/10 shadow-inner">
                  <AlertCircle className="w-10 h-10" />
                </div>
                <h3 className="text-3xl font-bold" style={{ fontFamily: THEME.fontSerif }}>
                  Event-Limit erreicht!
                </h3>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                <div className="space-y-2 text-center">
                  <p className="text-xl text-slate-700">
                    Du hast dein Limit von <span className="font-bold text-slate-900">{limitInfo.max} Event</span> erreicht.
                  </p>
                  <p className="text-slate-500">
                    Um neue Events zu erstellen und Premium-Features zu nutzen, benötigst du ein größeres Paket.
                  </p>
                </div>

                {/* Stats Card */}
                <div className="bg-orange-50 rounded-2xl p-5 border border-orange-100 flex items-center justify-between shadow-sm">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-orange-600 block mb-1">
                      Aktuell genutzte Pakete
                    </span>
                    <span className="font-bold text-slate-900 text-lg">{limitInfo.package}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold uppercase tracking-wider text-orange-600 block mb-1">
                      Auslastung
                    </span>
                    <span className="font-bold text-slate-900 text-lg">
                      {limitInfo.current} / {limitInfo.max}
                    </span>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    onClick={() => {
                      setShowLimitModal(false);
                      navigate("/packages");
                    }}
                    className="h-14 rounded-xl bg-slate-900 text-white font-bold text-lg hover:bg-slate-800 shadow-xl transition-all"
                  >
                    🚀 Jetzt upgraden
                  </Button>
                  <Button
                    onClick={() => {
                      setShowLimitModal(false);
                      navigate("/dashboard");
                    }}
                    variant="ghost"
                    className="h-12 rounded-xl text-slate-500 hover:text-slate-900"
                  >
                    Vielleicht später
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
